// src/components/admin/AllUsers.jsx

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import UserManager from "../../services/dashboard/UserManager";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileCsv, FaFileExcel } from "react-icons/fa";
import { UserPlus, Key, Trash2, MoreVertical, Eye, Edit, Filter, X } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CreateUserModal from "./CreateUserModal";
import ResetPasswordModal from "./ResetPasswordModal";
import DeleteUserModal from "./DeleteUserModal";
import ViewProfileModal from "./ViewProfileModal";
import EditProfileModal from "./EditProfileModal";
import toast from "react-hot-toast";

const AllUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Track the current search term
  const [searchTerm, setSearchTerm] = useState("");

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [platformFilter, setPlatformFilter] = useState("all"); // all, Android, iOS, web
  const [subscriptionFilter, setSubscriptionFilter] = useState("all"); // all, subscribed, notSubscribed
  const [planFilter, setPlanFilter] = useState("all"); // all, specific plan names
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewProfileModal, setShowViewProfileModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({});

  // Fetch all users (initial load or when resetting)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await UserManager.getAllUsersWithSubscriptionInfo();
      console.log("Fetched users data:", data);
      console.log("First user sample:", data[0]);
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  // On component mount, fetch all users once
  useEffect(() => {
    fetchUsers();
  }, []);

  // Debounced search function
  const performSearch = useCallback(
    async (query) => {
      if (query.trim() === "") {
        // If query is empty, reload full list
        fetchUsers();
        return;
      }

      try {
        setLoading(true);
        const results = await UserManager.searchUsers(query.trim());
        setUsers(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error("Search failed:", error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Wrap the search call in a debounced function (300ms delay)
  const debouncedSearch = useMemo(() => debounce(performSearch, 300), [
    performSearch,
  ]);

  // When searchTerm changes, call the debounced search
  useEffect(() => {
    debouncedSearch(searchTerm);

    // Clean up on unmount: cancel any pending debounce
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // CSV/Excel export helpers - Export filtered data
  const mapUsersForExport = () => {
    return filteredUsers.map((u) => ({
      Name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
      Email: u.emailId || "",
      Phone: u.mobileNumber || "",
      City: u.cityName || "",
      Pincode: u.pincode || "",
      Platform: u.platform || "",
      "Registered Date": u.creationDate ? formatDateTimeFromArray(u.creationDate) : "",
      "Subscribed": u.isSubscribed ? "Yes" : "No",
      "Subscription Status": u.subscriptionStatus || "N/A",
      "Plan Name": u.planName || "N/A",
      "Package Name": u.packageName || "N/A",
      "Remaining Days": u.remainingDays || "N/A",
    }));
  };

  const formatDateTimeFromArray = (arr) => {
    // Handle string format (ISO string like "2026-07-27T20:51:22.751021")
    if (typeof arr === 'string') {
      const date = new Date(arr);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
    
    // Handle array format [year, month, day, hour, minute, second]
    if (!Array.isArray(arr) || arr.length < 6) return "";
    
    const dateTime = new Date(
      arr[0],        // year
      arr[1] - 1,    // month (0-indexed)
      arr[2],        // day
      arr[3] || 0,   // hour
      arr[4] || 0,   // minute
      arr[5] || 0    // second
    ).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
    
    return dateTime;
  };

  const handleDownloadCSV = () => {
    const exportData = mapUsersForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "all_users.csv");
  };

  const handleDownloadExcel = () => {
    const exportData = mapUsersForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "all_users.xlsx");
  };

  // User management handlers
  const handleCreateUser = () => {
    setShowCreateModal(true);
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setShowViewProfileModal(true);
    setDropdownOpen(null);
  };

  const handleEditProfile = (user) => {
    setSelectedUser(user);
    setShowEditProfileModal(true);
    setDropdownOpen(null);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
    setDropdownOpen(null);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setDropdownOpen(null);
  };

  const handleUserCreated = () => {
    fetchUsers(); // Refresh the user list
    toast.success('User list refreshed');
  };

  const handleUserUpdated = () => {
    fetchUsers(); // Refresh the user list
    toast.success('User list refreshed');
  };

  const handlePasswordReset = () => {
    fetchUsers(); // Refresh the user list
    toast.success('User list refreshed');
  };

  const handleUserDeleted = () => {
    fetchUsers(); // Refresh the user list
    toast.success('User list refreshed');
  };

  const toggleDropdown = (userId, event) => {
    if (dropdownOpen === userId) {
      setDropdownOpen(null);
      setDropdownPosition({});
      return;
    }

    // Calculate position to avoid going off-screen and use fixed coordinates
    // to prevent getting clipped by the table's overflow-hidden container
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Approximate height of dropdown with 4 items
    
    // Check if dropdown would go below viewport
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const shouldPositionUp = spaceBelow < dropdownHeight + 20; // 20px buffer
    
    setDropdownPosition({
      shouldPositionUp,
      userId,
      top: buttonRect.bottom,
      bottom: viewportHeight - buttonRect.top,
      right: window.innerWidth - buttonRect.right
    });
    setDropdownOpen(userId);
  };

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClose = () => {
      setDropdownOpen(null);
      setDropdownPosition({});
    };
    
    if (dropdownOpen) {
      document.addEventListener('click', handleClose);
      window.addEventListener('scroll', handleClose, true); // true for capture phase
      return () => {
        document.removeEventListener('click', handleClose);
        window.removeEventListener('scroll', handleClose, true);
      };
    }
  }, [dropdownOpen]);

  // Extract unique plan names for filter
  const uniquePlans = useMemo(() => {
    const plans = users
      .map(u => u.planName)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i);
    return plans;
  }, [users]);

  // Apply filters to users
  const filteredUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      // Status filter (ACTIVE/INACTIVE)
      if (statusFilter === "active") {
        const userStatusValue = user.userStatus || "ACTIVE"; // Default to ACTIVE if undefined
        const isActive = userStatusValue === "ACTIVE";
        if (!isActive) {
          return false;
        }
      }
      if (statusFilter === "inactive") {
        const userStatusValue = user.userStatus || "ACTIVE"; // Default to ACTIVE if undefined
        const isInactive = userStatusValue === "INACTIVE";
        if (!isInactive) {
          return false;
        }
      }

      // Platform filter
      if (platformFilter !== "all" && user.platform !== platformFilter) return false;

      // Subscription filter
      if (subscriptionFilter === "subscribed" && !user.isSubscribed) return false;
      if (subscriptionFilter === "notSubscribed" && user.isSubscribed) return false;

      // Plan filter
      if (planFilter !== "all" && user.planName !== planFilter) return false;

      // Date range filter
      if (startDate || endDate) {
        if (!user.creationDate) return false;
        
        // Handle both string and array formats
        const userDate = typeof user.creationDate === 'string' 
          ? new Date(user.creationDate)
          : new Date(
              user.creationDate[0],
              user.creationDate[1] - 1,
              user.creationDate[2]
            );

        if (startDate && userDate < startDate) return false;
        if (endDate) {
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (userDate > endOfDay) return false;
        }
      }

      return true;
    });
    
    // Sort by creation date - newest first
    const sorted = filtered.sort((a, b) => {
      // Handle missing dates
      if (!a.creationDate) return 1;
      if (!b.creationDate) return -1;
      
      // Handle both string and array formats
      const dateA = typeof a.creationDate === 'string'
        ? new Date(a.creationDate)
        : new Date(
            a.creationDate[0],
            a.creationDate[1] - 1,
            a.creationDate[2],
            a.creationDate[3] || 0,
            a.creationDate[4] || 0,
            a.creationDate[5] || 0
          );
      
      const dateB = typeof b.creationDate === 'string'
        ? new Date(b.creationDate)
        : new Date(
            b.creationDate[0],
            b.creationDate[1] - 1,
            b.creationDate[2],
            b.creationDate[3] || 0,
            b.creationDate[4] || 0,
            b.creationDate[5] || 0
          );
      
      // Sort descending (newest first)
      return dateB - dateA;
    });
    
    return sorted;
  }, [users, statusFilter, platformFilter, subscriptionFilter, planFilter, startDate, endDate]);

  const clearFilters = () => {
    setStatusFilter("all");
    setPlatformFilter("all");
    setSubscriptionFilter("all");
    setPlanFilter("all");
    setStartDate(null);
    setEndDate(null);
  };

  const hasActiveFilters = statusFilter !== "all" || platformFilter !== "all" || subscriptionFilter !== "all" || planFilter !== "all" || startDate || endDate;

  // Subscription status badge component - Final Premium Polish
  const SubscriptionBadge = ({ user }) => {
    const isSubscribed = user.isSubscribed !== undefined ? user.isSubscribed : false;
    
    if (!isSubscribed) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-50/60 dark:bg-gray-800/30 text-gray-600 dark:text-gray-400 border border-gray-200/40 dark:border-gray-700/30 shadow-sm backdrop-blur-sm transition-all duration-150">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500"></span>
          No
        </span>
      );
    }

    const isActive = user.subscriptionStatus === 'ACTIVE';
    const badgeClass = isActive 
      ? "bg-green-50/50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200/40 dark:border-green-800/30"
      : "bg-red-50/50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200/40 dark:border-red-800/30";
    
    const dotClass = isActive
      ? "bg-green-500 dark:bg-green-400 shadow-[0_0_3px_rgba(34,197,94,0.5)]"
      : "bg-red-500 dark:bg-red-400 shadow-[0_0_3px_rgba(239,68,68,0.5)]";

    return (
      <div className="flex flex-col space-y-0.5">
        <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md text-[11px] font-medium border shadow-sm backdrop-blur-sm transition-all duration-150 ${badgeClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
          {isActive ? 'Active' : 'Expired'}
        </span>
        {user.planName && (
          <span className="text-[10px] text-gray-600 dark:text-gray-400 truncate max-w-32 font-medium" title={user.planName}>
            {user.planName}
          </span>
        )}
        {user.remainingDays !== null && user.remainingDays !== undefined && (
          <span className="text-[9px] text-gray-500 dark:text-gray-500 font-normal">
            {user.remainingDays > 0 ? `${user.remainingDays} days left` : 'Expired'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          ← Back
        </button>

        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              All Users
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage user accounts and subscriptions
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5 ${
                hasActiveFilters
                  ? "text-white bg-green-600 hover:bg-green-700"
                  : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters {hasActiveFilters && `(${filteredUsers.length})`}
            </button>
            <button
              onClick={handleCreateUser}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors shadow-sm"
            >
              <UserPlus className="w-4 h-4" />
              Create User
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Account Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Platform Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Platform
                </label>
                <select
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Platforms</option>
                  <option value="ANDROID">Android</option>
                  <option value="IOS">iOS</option>
                  <option value="WEB">Web</option>
                </select>
              </div>

              {/* Subscription Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subscription
                </label>
                <select
                  value={subscriptionFilter}
                  onChange={(e) => setSubscriptionFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Users</option>
                  <option value="subscribed">Subscribed</option>
                  <option value="notSubscribed">Not Subscribed</option>
                </select>
              </div>

              {/* Plan Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Plan
                </label>
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={uniquePlans.length === 0}
                >
                  <option value="all">All Plans</option>
                  {uniquePlans.map(plan => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>

              {/* Start Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Registration From
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={endDate || new Date()}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select start date"
                  isClearable
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* End Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Registration To
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  maxDate={new Date()}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select end date"
                  isClearable
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* Filter Summary */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing <span className="font-semibold text-orange-600 dark:text-orange-400">{filteredUsers.length}</span> of {users.length} users
                </p>
              </div>
            )}
          </div>
        )}

        {/* Search + Export Bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search by name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
            />
          </div>

          {/* Export Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-150 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              <FaFileCsv className="w-4 h-4" />
              Download CSV
            </button>
            <button
              onClick={handleDownloadExcel}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-150 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              <FaFileExcel className="w-4 h-4" />
              Download Excel
            </button>
          </div>
        </div>

        {/* Elite Table Container with Premium Polish */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading users...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-gray-200/60 dark:border-gray-700/50 overflow-hidden ring-1 ring-black/5 dark:ring-white/5">
            <div className="overflow-x-auto elite-scrollbar">
              <style>{`
                .elite-scrollbar::-webkit-scrollbar {
                  height: 6px;
                }
                .elite-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225);
                  border-radius: 3px;
                  transition: background-color 0.2s ease;
                }
                .dark .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105);
                }
                .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(148 163 184);
                }
                .dark .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              <table className="min-w-full">
                {/* Elite Header with Enhanced Contrast */}
                <thead className="bg-gray-100/80 dark:bg-gray-900/70 border-b border-gray-200 dark:border-gray-700/70 backdrop-blur-sm">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">User</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Contact</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Location</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Platform</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Subscription</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Registered</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                
                {/* Elite Premium Table Body - Final Polish */}
                <tbody className="bg-white dark:bg-gray-800">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <tr
                        key={user.userId}
                        className="group border-b border-gray-100/60 dark:border-white/[0.04] transition-all duration-200 ease-out hover:bg-gray-50/70 dark:hover:bg-white/[0.03] cursor-pointer"
                      >
                        {/* User Column - Name + Email */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-gray-950 dark:text-white transition-colors duration-150">
                              {user.firstName} {user.lastName}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                              {user.emailId}
                            </span>
                          </div>
                        </td>

                        {/* Contact Column */}
                        <td className="px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-150">
                          {user.mobileNumber}
                        </td>

                        {/* Location Column - City + Pincode */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium transition-colors duration-150">
                              {user.cityName || user.city?.cityName || "-"}
                            </span>
                            {user.pincode && (
                              <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                {user.pincode}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Platform Badge - Final Polish */}
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-medium backdrop-blur-sm transition-all duration-150 ${
                            user.platform === 'ANDROID' 
                              ? 'bg-green-50/50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200/40 dark:border-green-800/30'
                              : 'bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200/40 dark:border-blue-800/30'
                          } shadow-sm`}>
                            {user.platform}
                          </span>
                        </td>

                        {/* Subscription Status */}
                        <td className="px-3 py-2.5">
                          <SubscriptionBadge user={user} />
                        </td>

                        {/* Registered Date */}
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {user.creationDate ? formatDateTimeFromArray(user.creationDate) : "-"}
                          </span>
                        </td>

                        {/* Actions Column - Final Polish with Show on Hover */}
                        <td className="px-3 py-2.5 relative">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(user.userId, e);
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            
                            {dropdownOpen === user.userId && (
                              <div 
                                className={`fixed w-40 bg-white/98 dark:bg-gray-800/98 backdrop-blur-xl border border-gray-200/70 dark:border-gray-700/50 rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.15)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)] z-[100] overflow-hidden ${
                                  dropdownPosition.shouldPositionUp ? 'mb-1' : 'mt-1'
                                }`}
                                style={{
                                  top: dropdownPosition.shouldPositionUp ? 'auto' : `${dropdownPosition.top}px`,
                                  bottom: dropdownPosition.shouldPositionUp ? `${dropdownPosition.bottom}px` : 'auto',
                                  right: `${dropdownPosition.right}px`
                                }}
                              >
                                <button
                                  onClick={() => handleViewProfile(user)}
                                  className="w-full px-3 py-1.5 text-left hover:bg-gray-50/90 dark:hover:bg-gray-700/60 flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-150 text-xs border-b border-gray-100/80 dark:border-gray-700/40"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>View Profile</span>
                                </button>
                                <button
                                  onClick={() => handleEditProfile(user)}
                                  className="w-full px-3 py-1.5 text-left hover:bg-gray-50/90 dark:hover:bg-gray-700/60 flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-150 text-xs border-b border-gray-100/80 dark:border-gray-700/40"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                  <span>Edit Profile</span>
                                </button>
                                <button
                                  onClick={() => handleResetPassword(user)}
                                  className="w-full px-3 py-1.5 text-left hover:bg-gray-50/90 dark:hover:bg-gray-700/60 flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-150 text-xs border-b border-gray-100/80 dark:border-gray-700/40"
                                >
                                  <Key className="w-3.5 h-3.5" />
                                  <span>Reset Password</span>
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="w-full px-3 py-1.5 text-left hover:bg-red-50/90 dark:hover:bg-red-900/25 flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-all duration-150 text-xs"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete User</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUserCreated={handleUserCreated}
      />

      <ViewProfileModal
        isOpen={showViewProfileModal}
        onClose={() => setShowViewProfileModal(false)}
        user={selectedUser}
      />

      <EditProfileModal
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        user={selectedUser}
        onPasswordReset={handlePasswordReset}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        user={selectedUser}
        onUserDeleted={handleUserDeleted}
      />
    </div>
  );
};

export default AllUsers;